import React, { useEffect, useState, useRef } from 'react';
export function ParallaxHero() {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({
    rx: 0,
    ry: 0,
    tx: 0,
    ty: 0
  });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
      const y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
      setPos({
        rx: -y * 8,
        ry: x * 8,
        tx: x * 12,
        ty: y * 12
      });
    };
    const onLeave = () => setPos({
      rx: 0,
      ry: 0,
      tx: 0,
      ty: 0
    });
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);
  return <div ref={ref} className="w-full max-w-6xl mx-auto mb-24 cursor-pointer" style={{
    perspective: 1200
  }} data-id="element-437" data-mp="3fswrlockvvoa">
      <div className="relative w-full rounded-[2rem] overflow-hidden shadow-2xl" style={{
      transformStyle: 'preserve-3d',
      transform: `rotateX(${pos.rx}deg) rotateY(${pos.ry}deg)`,
      transition: 'transform 150ms ease-out'
    }} data-id="element-438" data-mp="1n3n1sxe6nyi5">
        {/* Layer 1: Background — blurred depth layer */}
        <div className="absolute inset-0 z-0" style={{
        transformStyle: 'preserve-3d',
        transform: `translateZ(-40px) translate(${pos.tx * 0.5}px, ${pos.ty * 0.5}px) scale(1.15)`,
        transition: 'transform 150ms ease-out'
      }} data-id="element-439" data-mp="186mgpcqhz1sn">
          <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1400&h=700&fit=crop&crop=center" alt="" className="w-full h-full object-cover blur-sm brightness-75" draggable={false} data-id="element-440" data-mp="1d3mbuslt6k4z" />
        </div>

        {/* Layer 2: Mid — gradient overlay with floating particles */}
        <div className="absolute inset-0 z-10" style={{
        transformStyle: 'preserve-3d',
        transform: `translateZ(15px) translate(${pos.tx * 0.8}px, ${pos.ty * 0.8}px)`,
        transition: 'transform 150ms ease-out'
      }} data-id="element-441" data-mp="30mdmebannwva">
          <div className="w-full h-full bg-gradient-to-br from-apple-blue/20 via-transparent to-purple-500/15" data-id="element-442" data-mp="3aigz2b34vdwh" />
          {/* Floating dots */}
          <div className="absolute top-[15%] left-[20%] w-3 h-3 bg-apple-blue/60 rounded-full animate-pulse" data-id="element-443" data-mp="2hx7cndruaflm" />
          <div className="absolute top-[30%] right-[25%] w-2 h-2 bg-purple-400/50 rounded-full animate-pulse" style={{
          animationDelay: '0.5s'
        }} data-id="element-444" data-mp="2mugyykjxmb58" />
          <div className="absolute bottom-[25%] left-[35%] w-2.5 h-2.5 bg-emerald-400/50 rounded-full animate-pulse" style={{
          animationDelay: '1s'
        }} data-id="element-445" data-mp="1u61vmva5xh9q" />
          <div className="absolute top-[55%] right-[15%] w-2 h-2 bg-amber-400/40 rounded-full animate-pulse" style={{
          animationDelay: '1.5s'
        }} data-id="element-446" data-mp="1rldloz933y82" />
          <div className="absolute bottom-[15%] right-[40%] w-3 h-3 bg-apple-blue/40 rounded-full animate-pulse" style={{
          animationDelay: '0.8s'
        }} data-id="element-447" data-mp="1hq27qj5i2v1d" />
        </div>

        {/* Layer 3: Front — main sharp image */}
        <div className="relative z-20" style={{
        transformStyle: 'preserve-3d',
        transform: `translateZ(40px) translate(${pos.tx * 1.2}px, ${pos.ty * 1.2}px)`,
        transition: 'transform 150ms ease-out'
      }} data-id="element-448" data-mp="13uzutc28k5f2">
          <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1400&h=700&fit=crop&crop=center" alt="Moderný technologický workspace" className="w-full h-64 md:h-[420px] object-cover" draggable={false} data-id="element-449" data-mp="oy8rirpfy9k6" />
          {/* Glass overlay with text */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-450" data-mp="1emxqxbgsioem">
            <div className="flex items-center gap-3 mb-2" data-id="element-451" data-mp="10u810vy54zc8">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-id="element-452" data-mp="14wbe1p0gkbzm" />
              <span className="text-white/80 text-xs font-mono uppercase tracking-wider" data-id="element-453" data-mp="qwtmj1vs2kmk">
                Gemini 2.0 Engine — Online
              </span>
            </div>
            <p className="text-white/90 text-sm md:text-base font-medium max-w-lg" data-id="element-454" data-mp="hxgdgabkfy2d">
              Multimodálna AI platforma spracováva text, obraz, video a zvuk v
              reálnom čase
            </p>
          </div>
        </div>

        {/* Shine/reflection effect */}
        <div className="absolute inset-0 z-30 pointer-events-none rounded-[2rem]" style={{
        background: `radial-gradient(circle at ${50 + pos.tx * 2}% ${50 + pos.ty * 2}%, rgba(255,255,255,0.15) 0%, transparent 60%)`,
        transition: 'background 150ms ease-out'
      }} data-id="element-455" data-mp="2c9d80c5b1k1n" />
      </div>
    </div>;
}